/**
 * Fast Loading Screen Component
 * 
 * Optimized loading screen with progress indication and timeout handling
 */

import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import { NetworkStatusIndicator } from './NetworkStatusIndicator';

interface FastLoadingScreenProps {
  message?: string;
  showProgress?: boolean;
  maxDuration?: number; // Maximum loading duration in ms
  onTimeout?: () => void;
}

export const FastLoadingScreen: React.FC<FastLoadingScreenProps> = ({
  message = 'Memuat...',
  showProgress = true,
  maxDuration = 5000,
  onTimeout
}) => {
  const [progress, setProgress] = useState(0);
  const [timeoutReached, setTimeoutReached] = useState(false);

  useEffect(() => {
    if (!showProgress) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        const increment = Math.random() * 15 + 5; // 5-20% increment
        const newProgress = Math.min(prev + increment, 90); // Max 90% until actual completion
        return newProgress;
      });
    }, 200);

    const timeout = setTimeout(() => {
      setTimeoutReached(true);
      onTimeout?.();
    }, maxDuration);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [showProgress, maxDuration, onTimeout]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="text-center space-y-6 max-w-md w-full">
        {/* Logo or Brand */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Canvango</h1>
          <p className="text-sm text-gray-600 mt-1">Member Area</p>
        </div>

        {/* Loading Spinner */}
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>

        {/* Loading Message */}
        <div className="space-y-2">
          <p className="text-sm text-gray-700 font-medium">{message}</p>
          
          {showProgress && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          
          {showProgress && (
            <p className="text-xs text-gray-500">
              {Math.round(progress)}% selesai
            </p>
          )}
        </div>

        {/* Network Status */}
        <div className="flex justify-center">
          <NetworkStatusIndicator showOnlyWhenIssues={true} />
        </div>

        {/* Timeout Message */}
        {timeoutReached && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
            <p className="text-sm text-yellow-800">
              Loading memakan waktu lebih lama dari biasanya. 
              Silakan periksa koneksi internet Anda.
            </p>
          </div>
        )}

        {/* Tips */}
        <div className="text-xs text-gray-500 space-y-1">
          <p>💡 Tips: Pastikan koneksi internet stabil</p>
          <p>🔄 Jika loading terlalu lama, coba refresh halaman</p>
        </div>
      </div>
    </div>
  );
};